import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Button } from '@/design-system/Button'
import { LanguageSwitcher } from '@/design-system/LanguageSwitcher'
import { Container, Overline, Skeleton } from '@/design-system/primitives'
import { usePublicEvent } from '@/features/event/api'
import {
  useCancelOrder,
  useMarkDone,
  useSetSoldOut,
  useSettle,
  useStationMenu,
  useStationOrders,
} from '@/features/service/api'
import {
  ChangeBanner,
  OrderCard,
  useErrorText,
  useWhere,
} from '@/features/service/components/OrderCard'
import type { SettleResult, StationMenuItem } from '@/features/service/types'
import { cn } from '@/lib/cn'
import { formatMoney } from '@/lib/money'
import { NoAccess, StaffGate, isForbidden } from '@/lib/staffGate'

type Scope = 'active' | 'done' | 'cancelled'
type Tab = Scope | 'menu'

/**
 * Écran de poste — cuisine ou bar.
 *
 * Une carte par **commande**, pas par ligne : le même numéro que le serveur,
 * la table, son nom, le serveur responsable, et les seules lignes de ce poste.
 * Une commande mixte apparaît aux deux postes, chacun avec ce qui le regarde.
 *
 * Rien n'arrive ici avant d'être réglé. Le chrono part du règlement (l'envoi)
 * et s'arrête à « Terminé » ; au-delà de dix minutes la carte s'allume, au-delà
 * de vingt elle crie. La plus ancienne est toujours en tête.
 *
 * On peut aussi y **encaisser** une commande (le client vient payer au bar) —
 * dans la caisse du poste, qu'il faut donc tenir — ou l'**annuler**, avec un
 * motif. Jamais la supprimer.
 *
 * L'onglet **Carte** dit ce qu'il n'y a plus : un produit marqué épuisé
 * disparaît de la commande à table, de l'écran des serveurs et de la caisse.
 */
export function StationPage({ station }: { station: 'kitchen' | 'bar' }) {
  const { t } = useTranslation()
  return (
    <StaffGate title={t(`station.${station}`)}>
      {(_session, signOut) => <StationBoard station={station} onSignOut={signOut} />}
    </StaffGate>
  )
}

function StationBoard({
  station,
  onSignOut,
}: {
  station: 'kitchen' | 'bar'
  onSignOut: () => void
}) {
  const { t, i18n } = useTranslation()
  const eventQuery = usePublicEvent()
  const eventId = eventQuery.data?.id
  const errorText = useErrorText()

  const [tab, setTab] = useState<Tab>('active')
  const scope: Scope = tab === 'menu' ? 'active' : tab
  const orders = useStationOrders(station, eventId, scope, true)
  const done = useMarkDone(station, eventId)
  const settle = useSettle(eventId, station)
  const cancel = useCancelOrder(eventId, station)
  const [lastSettle, setLastSettle] = useState<SettleResult | null>(null)
  const where = useWhere()

  // Un 403 n'est pas « rien en attente » : c'est un compte qui n'a pas ce
  // poste. Le dire évite de chercher une panne pendant que les tickets
  // s'accumulent au bon écran.
  if (isForbidden(orders.error)) {
    return <NoAccess title={t(`station.${station}`)} onSignOut={onSignOut} />
  }

  const list = orders.data?.orders ?? []
  const counts = orders.data?.counts
  const currency = list[0]?.currency ?? eventQuery.data?.currency ?? 'EUR'
  const money = (minor: number) => formatMoney({ amount_minor: minor, currency }, i18n.language)
  const busy = done.isPending || settle.isPending || cancel.isPending
  const actionError = errorText(done.error ?? settle.error ?? cancel.error)

  return (
    <Container className="py-6">
      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <div>
          {/* La langue à côté du nom de l'écran : le coin « compte » garde
              sa place, et rien ne pousse « Se déconnecter » hors de l'écran. */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Overline>{t(`station.${station}`)}</Overline>
            <LanguageSwitcher />
          </div>
          <h1 className="mt-2 font-display text-heading-lg text-content-primary">
            {t('stationOrders.title')}
          </h1>
        </div>
        <div className="text-right">
          <span className="block font-display text-display-md tabular-nums text-champagne-300">
            {counts?.waiting ?? 0}
          </span>
          {/* La caisse du poste : apports, sorties, remises des serveurs. */}
          <Link
            to="/ops/pos"
            className="flex min-h-10 items-center justify-end text-sm text-champagne-300 underline"
          >
            {t('stationOrders.till')}
          </Link>
          <Button variant="ghost" size="sm" onClick={onSignOut}>
            {t('checkin.logout')}
          </Button>
        </div>
      </div>

      {/* Quatre onglets ne tiennent pas sur 375 px sans couper les mots :
          deux rangées sur téléphone. */}
      <nav className="mt-5 grid grid-cols-2 gap-1 rounded-3xl border border-line-subtle p-1 sm:grid-cols-4 sm:rounded-full">
        {(
          [
            ['active', t('stationOrders.tabs.active'), (counts?.waiting ?? 0) + (counts?.unpaid ?? 0)],
            ['done', t('stationOrders.tabs.done'), counts?.done ?? 0],
            ['cancelled', t('stationOrders.tabs.cancelled'), counts?.cancelled ?? 0],
            ['menu', t('stationOrders.tabs.menu'), null],
          ] as const
        ).map(([key, label, count]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={cn(
              'flex min-h-11 min-w-0 items-center justify-center gap-1 rounded-full px-2 text-center text-sm leading-tight',
              tab === key ? 'bg-ember text-white' : 'text-content-secondary',
            )}
          >
            <span className="min-w-0">{label}</span>
            {count !== null ? (
              <span className="rounded-full bg-black/30 px-1.5 font-mono text-caption">{count}</span>
            ) : null}
          </button>
        ))}
      </nav>

      {tab === 'menu' ? (
        <StationMenu station={station} eventId={eventId} />
      ) : (
        <>
          {lastSettle ? (
            <div className="mt-4 max-w-md">
              <ChangeBanner
                orderNumber={lastSettle.order.order_number}
                table={where(lastSettle.order).title}
                stations={lastSettle.order.stations}
                change={lastSettle.change_minor}
                register={lastSettle.register_label}
                money={money}
                onDismiss={() => setLastSettle(null)}
              />
            </div>
          ) : null}
          {actionError ? <p className="mt-4 text-sm text-state-danger">{actionError}</p> : null}

          {orders.isPending ? (
            <Skeleton className="mt-8 h-40 w-full" />
          ) : list.length === 0 ? (
            <p className="mt-12 text-center text-[clamp(1.25rem,3vw,2rem)] text-content-muted">
              {t(`stationOrders.empty.${scope}`)}
            </p>
          ) : (
            <ul className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {list.map((order) => (
                <OrderCard
                  key={order.order_id}
                  order={order}
                  station={station}
                  money={money}
                  busy={busy}
                  onDone={() => done.mutate(order.order_id)}
                  onSettle={
                    order.status === 'pending_payment'
                      ? (payload) =>
                          settle.mutate(
                            { orderId: order.order_id, ...payload },
                            { onSuccess: (result) => setLastSettle(result) },
                          )
                      : undefined
                  }
                  onCancel={
                    scope !== 'cancelled'
                      ? (reason) => cancel.mutate({ orderId: order.order_id, reason })
                      : undefined
                  }
                />
              ))}
            </ul>
          )}
        </>
      )}
    </Container>
  )
}

/**
 * La carte du poste : ce qu'il prépare, et un interrupteur par produit.
 *
 * « Épuisé » ne touche pas au stock : c'est la vente qui s'arrête, partout à
 * la fois. Le geste inverse remet le produit en vente tel qu'il était.
 */
function StationMenu({
  station,
  eventId,
}: {
  station: 'kitchen' | 'bar'
  eventId: string | undefined
}) {
  const { t, i18n } = useTranslation()
  const errorText = useErrorText()
  const menu = useStationMenu(station, eventId, true)
  const toggle = useSetSoldOut(station, eventId)

  if (menu.isPending) return <Skeleton className="mt-8 h-40 w-full" />
  if (isForbidden(menu.error)) return <NoAccess inline />

  const rows = menu.data ?? []
  const time = (value: string | null) =>
    value
      ? new Date(value).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })
      : ''
  const soldOutCount = rows.filter((row) => row.sold_out).length

  return (
    <section className="mt-6">
      <p className="max-w-2xl text-sm text-content-secondary">{t('stationOrders.menu.hint')}</p>
      {soldOutCount > 0 ? (
        <p className="mt-2 text-sm text-ember-300">
          {t('stationOrders.menu.soldOutCount', { count: soldOutCount })}
        </p>
      ) : null}
      {toggle.isError ? (
        <p className="mt-3 text-sm text-state-danger">
          {isForbidden(toggle.error) ? t('stationOrders.menu.noRight') : errorText(toggle.error)}
        </p>
      ) : null}
      {rows.length === 0 ? (
        <p className="mt-8 text-center text-content-muted">{t('stationOrders.menu.empty')}</p>
      ) : (
        <ul className="mt-4 divide-y divide-line-subtle rounded-lg border border-line-subtle">
          {rows.map((row: StationMenuItem) => (
            <li key={`${row.kind}:${row.ref_id}`} className="flex items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p
                  className={cn(
                    'truncate text-content-primary',
                    row.sold_out && 'text-content-muted line-through',
                  )}
                >
                  {row.label}
                </p>
                <p className="text-caption text-content-muted">
                  {formatMoney({ amount_minor: row.price_minor, currency: row.currency }, i18n.language)}
                  {row.stock_remaining !== null
                    ? ` · ${t('stationOrders.menu.stock', { count: row.stock_remaining })}`
                    : ''}
                  {row.sold_out && row.sold_out_at
                    ? ` · ${t('stationOrders.menu.since', { time: time(row.sold_out_at) })}`
                    : ''}
                </p>
              </div>
              <Button
                size="sm"
                variant={row.sold_out ? 'champagne' : 'outline'}
                disabled={toggle.isPending}
                onClick={() =>
                  toggle.mutate({ kind: row.kind, ref_id: row.ref_id, sold_out: !row.sold_out })
                }
              >
                {row.sold_out ? t('stationOrders.menu.markAvailable') : t('stationOrders.menu.markSoldOut')}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
